/**
 * ⚡ 성능 계산 (IOMeter) — 엑셀 「Performance-IOMeter」 시트 (v2.233.0)
 *   A. 목표 성능에 필요한 디스크 수 — 9단계(랜덤/순차 → I/O 종류 → RAID → 디스크 종류 → 블록 → 단위 → 목표 → RAID 폼팩터 → JBOD 폼팩터)
 *   B. 디스크 수로 낼 수 있는 성능 — 같은 조건 + 디스크 수 → 디스크 1개 성능 × 디스크 수
 * 선택지는 성능 표(219줄)에 있는 조합만 보여 준다(OLTP 는 랜덤에만 · 랜덤 쓰기에는 SSD 줄이 없다 등). 앞 단계를 바꿔
 * 뒤 단계의 값이 표에 없어지면 첫 선택지로 옮긴다.
 */
import { useState } from 'react';
import {
  calcIometerDrives, calcIometerPerf, perfBlockOptions, perfDriveOptions, perfIoOptions, perfRaidOptions, findPerfRow,
} from '../../lib/sizing/calc';
import { BLOCK_ORDER, DRIVE_LABEL, IO_LABEL, JBOD_FORM_FACTORS, RAID_FORM_FACTORS, RAID_TO_JBOD, RANDOM_SEQ_LABEL } from '../../lib/sizing/data';
import type { BlockSize, DriveType, IoType, JbodFormFactor, PerfUnit, RaidFormFactor, RaidLevel, RandomSeq } from '../../lib/sizing/types';
import { Field, Formula, NumInput, PAIR_GRID, Panel, SelectInput, SimpleTable, Stat, StatGrid, fmt } from './SizingUi';

interface Sel { rs: RandomSeq; io: IoType; raid: RaidLevel; drive: DriveType; block: BlockSize; unit: PerfUnit }

/** 앞 단계 선택에 맞춰 뒤 단계를 표에 있는 값으로 맞춘다 */
function effective(s: Sel): Sel {
  const ioOpts = perfIoOptions(s.rs);
  const io = ioOpts.includes(s.io) ? s.io : ioOpts[0]!;
  const raidOpts = perfRaidOptions(s.rs, io);
  const raid = raidOpts.includes(s.raid) ? s.raid : raidOpts[0]!;
  const driveOpts = perfDriveOptions(s.rs, io, raid);
  const drive = driveOpts.includes(s.drive) ? s.drive : driveOpts[0]!;
  const blockOpts = perfBlockOptions({ rs: s.rs, io, raid, drive });
  const block = blockOpts.includes(s.block) ? s.block : blockOpts[0]!;
  return { ...s, io, raid, drive, block };
}

function pick<T extends string>(opts: readonly T[], label: (v: T) => string): { value: T; label: string }[] {
  return opts.map((v) => ({ value: v, label: label(v) }));
}
const RAID_LABEL: Record<RaidLevel, string> = { RAID1: 'RAID 1', RAID5: 'RAID 5', RAID6: 'RAID 6' };
const sortBlocks = (xs: BlockSize[]): BlockSize[] => [...xs].sort((a, b) => BLOCK_ORDER.indexOf(a) - BLOCK_ORDER.indexOf(b));

function StepFields({ s, setS }: { s: Sel; setS: (next: Sel) => void }) {
  const e = effective(s);
  return (
    <>
      <Field label="1. 랜덤 / 순차"><SelectInput value={e.rs} options={pick(['Random', 'Sequential'] as const, (v) => RANDOM_SEQ_LABEL[v])} onChange={(v) => setS({ ...e, rs: v })} /></Field>
      <Field label="2. I/O 종류" hint="OLTP 는 랜덤에만"><SelectInput value={e.io} options={pick(perfIoOptions(e.rs), (v) => IO_LABEL[v])} onChange={(v) => setS({ ...e, io: v })} /></Field>
      <Field label="3. RAID 레벨"><SelectInput value={e.raid} options={pick(perfRaidOptions(e.rs, e.io), (v) => RAID_LABEL[v])} onChange={(v) => setS({ ...e, raid: v })} /></Field>
      <Field label="4. 디스크 종류"><SelectInput value={e.drive} options={pick(perfDriveOptions(e.rs, e.io, e.raid), (v) => DRIVE_LABEL[v])} onChange={(v) => setS({ ...e, drive: v })} /></Field>
      <Field label="5. 블록 크기"><SelectInput value={e.block} options={pick(sortBlocks(perfBlockOptions(e)), (v) => v)} onChange={(v) => setS({ ...e, block: v })} /></Field>
      <Field label="6. 단위"><SelectInput value={e.unit} options={[{ value: 'IOPS', label: 'IOPS (초당 I/O 수)' }, { value: 'MB/s', label: 'MB/s (초당 전송량)' }]} onChange={(v) => setS({ ...e, unit: v })} /></Field>
    </>
  );
}

/** 이 조합에서 블록 크기별 디스크 1개 성능 — 표를 그대로 보여 준다 */
function BlockTable({ s }: { s: Sel }) {
  const blocks = sortBlocks(perfBlockOptions(s));
  const rows = blocks.map((b) => findPerfRow({ ...s, block: b })).filter((r): r is NonNullable<typeof r> => !!r);
  return (
    <details className="mt-3 text-sm">
      <summary className="cursor-pointer text-slate-500 hover:text-slate-700">📊 이 조합의 블록 크기별 디스크 1개 성능</summary>
      <div className="mt-1.5">
        <SimpleTable head={['블록', 'IOPS', 'MB/s']} align={['l', 'r', 'r']} rows={rows.map((r) => [r.block === s.block ? <b key="b">{r.block} ◀</b> : r.block, fmt(r.iops, 2), fmt(r.mbps, 3)])} />
      </div>
    </details>
  );
}

export function PerfCalc() {
  const [a, setA] = useState<Sel>({ rs: 'Random', io: 'OLTP', raid: 'RAID5', drive: 'Toshiba SSD', block: '4K', unit: 'IOPS' });
  const [target, setTarget] = useState(50000);
  const [raidFF, setRaidFF] = useState<RaidFormFactor>('RAID3U16');
  const [jbodFF, setJbodFF] = useState<JbodFormFactor>('4U48');
  const [b, setB] = useState<Sel>({ rs: 'Sequential', io: 'SequentialWrite', raid: 'RAID5', drive: 'Toshiba SSD', block: '1M', unit: 'MB/s' });
  const [drives, setDrives] = useState(24);

  const ea = effective(a);
  const jbodAllowed = RAID_TO_JBOD[raidFF];
  const jbodEff = jbodAllowed.includes(jbodFF) ? jbodFF : jbodAllowed[0]!;
  const ra = calcIometerDrives({ ...ea, target, raidFF, jbodFF: jbodEff });
  const eb = effective(b);
  const rb = calcIometerPerf({ ...eb, drives });
  const unitA = ea.unit;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="ui-modal-title mb-1">A. 목표 성능에 필요한 디스크 수</h2>
        <p className="text-sm text-slate-500 mb-3">아홉 단계를 차례로 고른다 — 엑셀 원본의 「모든 단계를 다 골라야 계산된다」와 같다. 성능 값은 인포트렌드가 IOMeter 로 잰 디스크 1개 기준이다.</p>
        <div className={PAIR_GRID}>
          <Panel title="입력" tone="input">
            <StepFields s={a} setS={setA} />
            <Field label="7. 목표 성능"><NumInput value={target} min={1} step={1} onChange={setTarget} suffix={unitA} /></Field>
            <Field label="8. RAID 인클로저 폼팩터"><SelectInput value={raidFF} options={RAID_FORM_FACTORS.map((f) => ({ value: f.key, label: f.label }))} onChange={setRaidFF} /></Field>
            <Field label="9. JBOD 폼팩터" hint="RAID 폼팩터에 이을 수 있는 것만"><SelectInput value={jbodEff} options={JBOD_FORM_FACTORS.filter((f) => jbodAllowed.includes(f.key)).map((f) => ({ value: f.key, label: f.label }))} onChange={setJbodFF} /></Field>
          </Panel>
          <Panel title="결과" tone="result">
            {ra ? (
              <>
                <StatGrid>
                  <Stat label="디스크 1개 성능" value={fmt(ra.perDisk, unitA === 'IOPS' ? 0 : 3)} unit={unitA} />
                  <Stat label="정확히 필요한 디스크 수" value={fmt(ra.exact)} unit="개" big />
                  <Stat label={`RAID 인클로저 권장 디스크 수 (${ra.raidBay}베이)`} value={fmt(ra.raidRecommended)} unit="개" />
                  <Stat label="JBOD 에 정확히 필요한 디스크 수" value={fmt(ra.jbodExact)} unit="개" />
                  <Stat label={`JBOD 권장 디스크 수${ra.jbodBay ? ` (${ra.jbodBay}베이)` : ''}`} value={ra.jbodRecommended === null ? '—' : fmt(ra.jbodRecommended)} unit="개" />
                  <Stat label="권장 총 디스크 수" value={ra.total === null ? '—' : fmt(ra.total)} unit="개" big tone={ra.total === null ? 'warn' : 'ok'} />
                </StatGrid>
                {ra.message && <p className="text-sm text-red-700 mt-3">⚠ {ra.message}</p>}
                <p className="text-xs text-slate-500 mt-3 leading-relaxed">「권장」은 논리 드라이브를 만들기 좋게 인클로저(또는 서랍)를 가득 채운 수다. 이중화 컨트롤러 모델이면 논리 드라이브 절반은 컨트롤러 A, 나머지는 B 에 둔다.</p>
                <BlockTable s={ea} />
                <Formula>
                  <p>정확히 필요한 수 = 올림(목표 ÷ 디스크 1개 성능)</p>
                  <p>RAID 권장 = RAID 폼팩터 베이 수 · JBOD 필요 = 정확한 수 − RAID 권장 (음수면 0)</p>
                  <p>JBOD 권장 = JBOD 필요를 단위(12·24·16베이 인클로저 또는 48·60베이의 서랍 16·20개)의 배수로 올림 · 최대 12·16·24베이 14대 · 48베이 6대 · 60베이 7대</p>
                </Formula>
              </>
            ) : <p className="text-sm text-slate-500">표에 없는 조합입니다.</p>}
          </Panel>
        </div>
      </div>

      <div>
        <h2 className="ui-modal-title mb-1">B. 디스크 수로 낼 수 있는 성능</h2>
        <p className="text-sm text-slate-500 mb-3">「디스크가 이만큼 있다」에서 출발 — 그 조건에서 나오는 총 성능.</p>
        <div className={PAIR_GRID}>
          <Panel title="입력" tone="input">
            <StepFields s={b} setS={setB} />
            <Field label="7. 디스크 수"><NumInput value={drives} min={1} step={1} onChange={setDrives} suffix="개" /></Field>
          </Panel>
          <Panel title="결과" tone="result">
            {rb ? (
              <>
                <StatGrid>
                  <Stat label="디스크 1개 성능" value={fmt(rb.perDisk, eb.unit === 'IOPS' ? 0 : 3)} unit={eb.unit} />
                  <Stat label={`총 성능 (${fmt(drives)}개)`} value={fmt(rb.total, eb.unit === 'IOPS' ? 0 : 1)} unit={eb.unit} big tone="ok" />
                </StatGrid>
                <BlockTable s={eb} />
                <Formula><p>총 성능 = 디스크 1개 성능 × 디스크 수 (표의 값은 인클로저 수와 무관하게 디스크 수에 비례한다고 본다)</p></Formula>
              </>
            ) : <p className="text-sm text-slate-500">표에 없는 조합입니다.</p>}
          </Panel>
        </div>
      </div>
    </div>
  );
}
